import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ButtonLink from '../ButtonLink';
import Credential from './Credential';
import Expired from './Expired';
import Inactive from './Inactive';
import WalletLegalIdCard from './WalletLegalIdCard';
import WalletLegalIdDetailModal from './WalletLegalIdDetailModal';
import IdProps from '../../proptypes/id';
import downArrow from '../../img/icons/down-arrow.svg';
import idIcon from '../../img/icons/id.svg';
import style from '../../style/components/wallet/MyIds.module.scss';
import { useSelector } from 'react-redux';

const MyIds = ({
  active,
  collapseActive,
  collapseExpired,
  collapseInactive,
  expired,
  inactive,
  isActiveCollapsed,
  isExpiredCollapsed,
  isInactiveCollapsed,
}) => {
  const { t } = useTranslation();
  const isMinor = useSelector((state) => state.user?.is_minor);
  const countryWorkProfile = useSelector(
    (state) => state.featureFlags.countryWorkProfile,
  );
  const inlineEmailEdit = useSelector(
    (state) => state.featureFlags.inlineEmailEdit,
  );
  // TODO(PM-1824/PM-1825): dummy wallet document cards + detail overlay
  // require both flags — see PM-1824/PM-1825 discussion.
  const showLegalIds = countryWorkProfile && inlineEmailEdit;
  const legalIds = useSelector((state) => state.countryWorkProfile.legalIds);
  const activeCount = active.length + (showLegalIds ? legalIds.length : 0);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);

  return (
    <div className={style.wrapper}>
      <main className={style.ids}>
        <h3 className={style.title}>
          <img alt="IDs" src={idIcon} />
          {t('credential.title')}
        </h3>
        {activeCount > 0 && (
          <Fragment>
            <h4 className={style.subtitle}>
              <ButtonLink
                onClick={collapseActive}
                aria-expanded={!isActiveCollapsed}
              >
                <img
                  alt=""
                  src={downArrow}
                  className={isActiveCollapsed ? style.collapsed : ''}
                />
                <span className="sr-only">
                  {isActiveCollapsed
                    ? t('credential.ariaLabel_expand')
                    : t('credential.ariaLabel_collapse')}
                </span>
              </ButtonLink>
              {t('credential.heading_active', { count: activeCount })}
            </h4>
            {!isActiveCollapsed && (
              <section className={style.idList}>
                {active.map((credential) => (
                  <Credential key={credential.type} {...credential} />
                ))}
                {showLegalIds &&
                  legalIds.map((doc, index) => (
                    <WalletLegalIdCard
                      key={doc.kind}
                      variant={doc.kind}
                      onClick={() => setSelectedDocIndex(index)}
                      showCardHolderName
                    />
                  ))}
              </section>
            )}
          </Fragment>
        )}
        {showLegalIds && selectedDocIndex !== null && (
          <WalletLegalIdDetailModal
            documents={legalIds}
            selectedIndex={selectedDocIndex}
            isOpen
            onClose={() => setSelectedDocIndex(null)}
            onNavigate={setSelectedDocIndex}
          />
        )}
        {expired.length > 0 && (
          <Fragment>
            <h4 className={style.subtitle}>
              <ButtonLink
                onClick={collapseExpired}
                aria-expanded={!isExpiredCollapsed}
              >
                <img
                  alt=""
                  src={downArrow}
                  className={isExpiredCollapsed ? style.collapsed : ''}
                />
                <span className="sr-only">
                  {isExpiredCollapsed
                    ? t('credential.ariaLabel_expand')
                    : t('credential.ariaLabel_collapse')}
                </span>
              </ButtonLink>
              {t('credential.heading_expired', { count: expired.length })}
            </h4>
            {!isExpiredCollapsed && (
              <Fragment>
                <p className={style.error}>
                  {expired.length > 1
                    ? t('credential.description_expired_other')
                    : t('credential.description_expired_one')}
                </p>
                <section className={style.idList}>
                  {expired.map((credential) => (
                    <Expired key={credential.type} {...credential} />
                  ))}
                </section>
              </Fragment>
            )}
          </Fragment>
        )}
        {inactive.length > 0 && (
          <Fragment>
            <h4 className={style.subtitle}>
              <ButtonLink
                onClick={collapseInactive}
                aria-expanded={!isInactiveCollapsed}
              >
                <img
                  alt=""
                  src={downArrow}
                  className={isInactiveCollapsed ? style.collapsed : ''}
                />
                <span className="sr-only">
                  {isInactiveCollapsed
                    ? t('credential.ariaLabel_expand')
                    : t('credential.ariaLabel_collapse')}
                </span>
              </ButtonLink>
              {t('credential.heading_inactive', { count: inactive.length })}
            </h4>
            {!isInactiveCollapsed && isMinor && (
              <Fragment>
                <p>{t('credential.description_minorRestriction')}</p>
              </Fragment>
            )}
            {!isInactiveCollapsed && (
              <section className={style.idList}>
                {inactive.map((credential) => (
                  <Inactive
                    key={credential.type}
                    isMinor={isMinor}
                    {...credential}
                  />
                ))}
              </section>
            )}
          </Fragment>
        )}
      </main>
    </div>
  );
};

MyIds.propTypes = {
  active: PropTypes.arrayOf(
    PropTypes.shape({
      ...IdProps,
    }),
  ),
  collapseActive: PropTypes.func.isRequired,
  collapseExpired: PropTypes.func.isRequired,
  collapseInactive: PropTypes.func.isRequired,
  expired: PropTypes.arrayOf(
    PropTypes.shape({
      ...IdProps,
    }),
  ),
  inactive: PropTypes.arrayOf(
    PropTypes.shape({
      ...IdProps,
    }),
  ),
  isActiveCollapsed: PropTypes.bool.isRequired,
  isExpiredCollapsed: PropTypes.bool.isRequired,
  isInactiveCollapsed: PropTypes.bool.isRequired,
};

MyIds.defaultProps = {
  active: [],
  expired: [],
  inactive: [],
};

export default MyIds;
